import React, { useContext, useMemo } from 'react';
import type { RollingChartProps, RollingPeriodEnum } from './types';
import { getAnalysisLabels } from 'venn-utils';
import AnalysisLineChart from './internal/AnalysisLineChart';
import { AnalysisViewContext } from '../../contexts';
import { PERIODS } from './internal/AnalysisChartLegend';
import useCustomRollingWindow from './internal/useCustomRollingWindow';
import { isNil } from 'lodash';

const AnalysisRollingCorrelationChart = ({
  frequency,
  analysisGroup,
  subject,
  actualTimeFrame,
  relative,
  noChartErrorComponent,
  noChartTrackingProps,
  downloadMetaData,
  categoryActive,
}: RollingChartProps) => {
  const fullPeriodLabels = useMemo(() => {
    const titleType = getAnalysisLabels(subject.type).main;
    return {
      absolute: `${titleType}'s Full-History Correlation`,
      relative: `${titleType}'s Full-History Correlation`,
    };
  }, [subject.type]);

  const { rollingBenchmarkCorrelationPeriod, onUpdateAnalysisViewParam } = useContext(AnalysisViewContext);
  const { chartDataWithRollingPeriods, customPeriodLoading, setOptimisticLoading } = useCustomRollingWindow(
    subject,
    analysisGroup,
    rollingBenchmarkCorrelationPeriod,
    relative,
    categoryActive,
    actualTimeFrame,
    'ROLLING_BENCHMARK_CORRELATION',
    'rollingBenchmarkCorrelation',
    fullPeriodLabels,
    noChartTrackingProps?.trackingId,
  );

  const header = 'Rolling Correlation to Benchmark';
  return (
    <AnalysisLineChart
      defaultRollingPeriod={PERIODS.find(({ key }) => key === rollingBenchmarkCorrelationPeriod)}
      rollingPeriodSettings={rollingBenchmarkCorrelationPeriod}
      loadingSerieData={customPeriodLoading}
      onRollingPeriodChange={(updatedPeriodKey?: RollingPeriodEnum, customPeriodMonths?: number) => {
        if (!isNil(updatedPeriodKey)) {
          onUpdateAnalysisViewParam({ rollingBenchmarkCorrelationPeriod: { period: updatedPeriodKey } });
          return;
        }
        if (!isNil(customPeriodMonths)) {
          setOptimisticLoading(true);
          onUpdateAnalysisViewParam({ rollingBenchmarkCorrelationPeriod: { customMonths: customPeriodMonths } });
        }
      }}
      allowCustomWindow
      analysisSubject={subject}
      frequency={frequency}
      relative={relative}
      chartDataWithRollingPeriods={chartDataWithRollingPeriods}
      start={actualTimeFrame.startTime}
      end={actualTimeFrame.endTime}
      header={header}
      noChartErrorComponent={noChartErrorComponent}
      noChartTrackingProps={noChartTrackingProps}
      yAxisUnitFormat="ratio"
      exportPNGEnabled
      exportXLSXEnabled
      dataExportEvent={{
        description: 'ROLLING_BENCHMARK_CORRELATION',
        relativeToBenchmark: relative,
        subjectType: subject.type,
        subjectId: subject.id,
        userUploaded: subject.userUploaded,
      }}
      downloadMetaData={downloadMetaData}
    />
  );
};

export default AnalysisRollingCorrelationChart;
